import React from 'react';
import { OverlayDisplayDiv } from '@UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { Close } from '@mui/icons-material';
import { useAccountStore } from '@components/Stores/accountStore';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { getCurrencySymbol, useCurrencyRates } from '../../../hooks/useCurrencyRates';
import { formatToPriceInDollars } from '../../../utils/FormatValue';
import { Material } from '../../../sp-types';
import { getUnitObj } from '../../../utils/MassUnits/massUnitsUtils';
import { getDateBefore } from '../../../utils/gridUtils';
import { getHistoryPricesSince } from '../../../sp-firebase/historyPricesFB';
import { getCurveForIndex, PricePoint } from './HistoricalUtils';
import { CircularProgress } from '@mui/material';
import { CurveMiniChart } from './CurveMiniChart';

export const TodayPricePopup: React.FC<{ onClose: () => void; materialId: string }> = ({ onClose, materialId }) => {
  const { materialColorDictionary: colorsMap } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const { pricesMap, getCurrencyInUSD } = useCurrencyRates();
  const [cocoaRatios] = React.useState<string[]>(['RFVCBLEURADC1', 'RFVCLLEURADC1']);
  const [cocoaDict] = React.useState<Record<string, string>>({
    RFVCBLEUDC1: 'RFVCBLEURADC1',
    RFVCLLEUDC1: 'RFVCLLEURADC1',
  });

  return (
    <OverlayDisplayDiv hideClose setDisplayOverlay={onClose}>
      <div className=" flex flex-col p-3 gap-6 rounded bg-white ">
        <div className="flex items-center justify-between gap-6">
          <p>
            Current price of <b>1 {getUnitObj(materialsMap[materialId].unit).shortForm}</b>
            <span style={{ color: colorsMap[materialId] || '#000' }}> {materialsMap[materialId].name}:</span>{' '}
            {getCurrencySymbol(materialsMap[materialId].currency)}
            {materialsMap[materialId].currentPrice}
          </p>
          <Close className="cursor-pointer align-self-baseline text-purple me-1" onClick={onClose} />
        </div>

        <PriceCard
          isRatio={cocoaRatios.includes(materialId)}
          material={materialsMap[materialId]}
          usdPrice={pricesMap[materialId]}
          getCurrencyInUSD={getCurrencyInUSD}
        />

        {cocoaDict[materialId] && (
          <CocoaAddition
            ratio={materialsMap[cocoaDict[materialId]]}
            lccMat={materialsMap['LCCc1']}
            lccaUsd={pricesMap['LCCc1']}
            color={colorsMap['LCCc1'] || '#000'}
            getCurrencyInUSD={getCurrencyInUSD}
          />
        )}
      </div>
    </OverlayDisplayDiv>
  );
};

interface PriceCardProps {
  material: Material;
  usdPrice: number;
  getCurrencyInUSD: (_currency: string) => number;
  isRatio: boolean;
}

const PriceCard: React.FC<PriceCardProps> = ({ material, usdPrice, getCurrencyInUSD, isRatio }) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [futurePrices, setFuturePrices] = React.useState<PricePoint[]>([]);

  React.useEffect(() => {
    const fetchFuturePrices = async () => {
      setLoading(true);
      const fromDate = getDateBefore(10);

      const prices = await getHistoryPricesSince(material.id.replace('c1', '').replace('C1', '') || '', fromDate);
      if (prices?.length) {
        const curveData = getCurveForIndex(prices.length - 1, new Date(), prices);

        setFuturePrices(curveData);
      } else {
        setFuturePrices([]);
      }
      setLoading(false);
    };

    if (material?.id) {
      fetchFuturePrices();
    }
  }, [material?.id]);

  return (
    <div className="flex flex-col bg-white ">
      <div className="flex justify-between flex-col gap-6 flex-1 items-center">
        {!isRatio && <PricesRows material={material} usdPrice={usdPrice} getCurrencyInUSD={getCurrencyInUSD} />}
        <div className="flex flex-1 flex-col">
          <h6 className="flex text-xs text-gray-450 justify-center">Trend</h6>
          <div className="flex w-92 h-[5.5rem] items-center justify-center">
            {loading ? (
              <div className="flex items-center justify-center">
                <CircularProgress size={32} />
              </div>
            ) : (
              <CurveMiniChart
                routePrices={futurePrices}
                cssClass="w-92 h-24"
                currency={getCurrencySymbol(material.currency || '')}
                showTicks
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface PricesRowsProps {
  material: Material;
  usdPrice: number;
  getCurrencyInUSD: (_currency: string) => number;
}
export const PricesRows: React.FC<PricesRowsProps> = ({ usdPrice, getCurrencyInUSD }) => {
  return (
    <div className="flex gap-2 h-10">
      <div className="border-1 border-gray-150 p-2 rounded"> {formatToPriceInDollars(usdPrice)}</div>
      {['EUR', 'GBP', 'ILS'].map((currency) => (
        <div key={currency} className="border-1 border-gray-150 p-2 rounded ">
          {formatToPriceInDollars(usdPrice / getCurrencyInUSD(currency), 1, currency)}
        </div>
      ))}
    </div>
  );
};

interface CocoaProps {
  ratio: Material;
  lccMat: Material;
  lccaUsd: number;
  color: string;
  getCurrencyInUSD: (_currency: string) => number;
}
export const CocoaAddition: React.FC<CocoaProps> = ({ ratio, lccMat, lccaUsd, color, getCurrencyInUSD }) => (
  <>
    <div className="flex justify-center gap-3 mt-2 p-2 rounded border-1 border-gray-150  w-[250px] self-center">
      <p>{ratio.name}</p>
      <p>{ratio.currentPrice}</p>
    </div>
    <div className="flex flex-col flex-1 items-center justify-center my-2">
      <span className="flex gap-1">
        <p style={{ color }}>{lccMat.name}</p> current price:
      </span>
      <PricesRows material={lccMat} usdPrice={lccaUsd} getCurrencyInUSD={getCurrencyInUSD} />
    </div>
  </>
);
